import { theme, utils } from '@snsw/react-component-library';
import { useContext } from 'react';
import { ThemeContext } from 'styled-components';

// The Kiama Theme object looks like:
//
// {
//   breakpoints: (6) ['480px', '576px', '768px', '992px', '1200px', '1600px', xs: '480px', sm: '576px', md: '768px', lg: '992px', xl: '1200px', …]
//   colors: {white: '#ffffff', black: '#000000', red: '#d7153a', blue: '#2e5299', darkBlue: '#002664', …}
//   componentWidths: {screener: {…}}
//   media: {xs: 480, sm: 576, md: 768, lg: 992, xl: 1200, …}
//   name: "ETDB"
//   pageWidths: {lpBanner: '97.5rem', pageMax: '76.5rem', copyBlock: '55.625rem'}
//   space: (12) [0, 16, 32, 48, 62, 78, 48, 56, 62, 70, 78, 86, 0.5: 8, 1.5: 24, 2.5: 40, 3.5: 56, 4.5: 70, …]
// }

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export type KnownFontWeight =
  /** Corresponds to 325 or below in Figma */
  | 'normal'

  /** Corresponds to 350 in Figma */
  | 'semi-bold'

  /** Corresponds to 400+ in Figma */
  | 'bold';

// These are the same as our font weights, but Kiama calls them different names.
export type KiamaFontWeight = 'book' | 'medium' | 'bold';

export type KnownColor =
  | 'black'
  | 'blackBlue'
  | 'blue'
  | 'darkBlue'
  | 'grey'
  | 'darkGrey'
  | 'green'
  | 'greyBackground'
  | 'lightGrey1'
  | 'lightGrey2'
  | 'lightGrey3'
  | 'lightGrey4'
  | 'red'
  | 'white';

// Brand colors - https://kiama.testservicensw.net/storybook/?path=/story/foundations-colors--colors
export type BrandColor =
  | 'primaryBlue'
  | 'primaryRed'
  | 'secondaryBlue'
  | 'darkGrey'
  | 'shuttle'
  | 'geyser'
  | 'mystic'
  | 'iceBlue';

export type KnownFontSize = 'base' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

// GEL supports an additional font-size of 14px which Kiama does not handle.
export type CustomFontSize = 'xs';

type SpacingTokens = [number, number?, number?, number?];

const px = (value: number) => utils.pxToRem(value);

export const GelTheme = {
  color: (c: KnownColor | BrandColor) => {
    // The colors in the Gel theme are:
    //
    //   black: "#000000"
    //   blackBlue: "#242934"
    //   blue: "#2e5299"
    //   darkBlue: "#002664"
    //   darkGrey: "#646974"
    //   green: "#00a908"
    //   greyBackground: "#f4f7f9"
    //   lightGrey1: "#f4f7f9"
    //   lightGrey2: "#eaedf4"
    //   lightGrey3: "#9aa8af"
    //   lightGrey4: "#dee3e5"
    //   red: "#d7153a"
    //   white: "#ffffff"
    //
    // Brand colors are mostly aliased in the Gel theme
    switch (c) {
      case 'primaryBlue':
        return theme.colors.darkBlue;
      case 'primaryRed':
        return theme.colors.red;
      case 'secondaryBlue':
        return theme.colors.blue;
      case 'darkGrey':
        return theme.colors.blackBlue;
      case 'shuttle':
        return theme.colors.darkGrey;
      case 'geyser':
        return theme.colors.lightGrey4;
      case 'iceBlue':
        return theme.colors.lightGrey1;

      // We also support a lighter version of grey
      case 'grey':
        return '#848484';

      // True to it's name, mystic seems to be missing from the Gel theme
      case 'mystic':
        return '#ecf1f3';

      default:
        return theme.colors[c] ?? c;
    }
  },

  breakpoint: (b: Breakpoint) => theme.breakpoints[b],
  fontWeight: (w: KnownFontWeight | KiamaFontWeight) => {
    switch (w) {
      case 'medium':
      case 'semi-bold':
        return 500;
      case 'bold':
        return 700;
      case 'normal':
      case 'book':
      default:
        return 300;
    }
  },

  fontSize: (s: KnownFontSize | CustomFontSize) => {
    // Handle custom font sizes
    if (s === 'xs') {
      return `${utils.pxToRem(14)}`;
    }
    // Delegate known font sizes to Kiama
    return utils.getTypeSize(s);
  },

  spacing: (...tokens: SpacingTokens) =>
    tokens
      .map((token) => {
        if (Number.isInteger(token) && token < theme.space.length) {
          return px(theme.space[token]);
        }

        // The Kiama space token array also has predefined properties for "half tokens" e.g. '1.5'
        const presetValue = theme.space[token];
        if (presetValue != null) return px(presetValue);

        // If the user has passed a spacing token value that is unknown, then we'll just use it as a multiplier against
        // a single spacing token, but log a warning to this effect.
        const result = px(theme.space[1] * token);
        console.warn(`Unconventional spacing token '${token}' translated to '${result}'`);

        return result;
      })
      .join(' '),
};

export type Theme = typeof theme;
type ThemeProps = { theme?: Theme };

const getTheme = (props: ThemeProps) => (props.theme && props.theme.color ? props.theme : theme);
const getColor = (props: ThemeProps, color: KnownColor | BrandColor) => getTheme(props).color(color);
const getFontWeight = (props: ThemeProps, weight: KnownFontWeight) => getTheme(props).fontWeight(weight);
const getFontSize = (props: ThemeProps, size: KnownFontSize) => getTheme(props).fontSize(size);
const getSpacing = (props: ThemeProps, ...tokens: SpacingTokens) => getTheme(props).spacing(...tokens);

export const useTheme = () => {
  const theme = useContext(ThemeContext);

  const props = { theme };
  return {
    color: (name: KnownColor) => getColor(props, name),
    fontWeight: (weight: KnownFontWeight) => getFontWeight(props, weight),
    fontSize: (size: KnownFontSize) => getFontSize(props, size),
    spacing: (...tokens: SpacingTokens) => getSpacing(props, ...tokens),
  };
};
